// src/pages/communes/components/CommunalAdminCreateModal.tsx

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Alert,
  InputNumber,
  Switch,
  TimePicker,
  Collapse,
  Tooltip,
  Progress,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { 
  useCreateCommunalAdmin, 
  usePasswordUtils,
  useWorkingHours,
  useCheckEmailAvailability,
} from '../../../hooks/useCommunalAdmin';
import type { CommunalAdminCreateRequest } from '../../../services/communalAdmin.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

interface CommunalAdminCreateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const CommunalAdminCreateModal: React.FC<CommunalAdminCreateModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [passwordGenerated, setPasswordGenerated] = useState(false);

  // Hooks
  const { mutate: createCommunalAdmin, isPending: isCreating } = useCreateCommunalAdmin();
  const { generatePassword, validatePassword, validatePhone } = usePasswordUtils();
  const { getDefaultWorkingHours, formatWorkingHours } = useWorkingHours();
  const { mutate: checkEmail, isPending: checkingEmail } = useCheckEmailAvailability();

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setCurrentStep(0);
      setEmailAvailable(null);
      setPasswordGenerated(false);
    }
  }, [visible, form]);


  // Handle email change and availability check
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    if (email && email.includes('@')) {
      checkEmail(email, {
        onSuccess: (result) => {
          setEmailAvailable(result.available);
        },
        onError: () => {
          setEmailAvailable(null);
        },
      });
    } else {
      setEmailAvailable(null);
    }
  };

  // Generate secure password
  const handleGeneratePassword = () => {
    const newPassword = generatePassword(12);
    form.setFieldsValue({ password: newPassword });
    setPasswordGenerated(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Prepare working hours data
      const workingHours = values.includeWorkingHours ? {
        monday: values.workingHours?.monday || { start: '08:00', end: '17:00', isWorkingDay: true },
        tuesday: values.workingHours?.tuesday || { start: '08:00', end: '17:00', isWorkingDay: true },
        wednesday: values.workingHours?.wednesday || { start: '08:00', end: '17:00', isWorkingDay: true },
        thursday: values.workingHours?.thursday || { start: '08:00', end: '17:00', isWorkingDay: true },
        friday: values.workingHours?.friday || { start: '08:00', end: '17:00', isWorkingDay: true },
        saturday: values.workingHours?.saturday || { start: '08:00', end: '12:00', isWorkingDay: false },
        sunday: values.workingHours?.sunday || { start: '08:00', end: '12:00', isWorkingDay: false },
      } : undefined;

      // Prepare emergency contact data
      const emergencyContact = values.includeEmergencyContact ? {
        name: values.emergencyContact?.name,
        phone: values.emergencyContact?.phone,
        relationship: values.emergencyContact?.relationship,
      } : undefined;

      const payload: CommunalAdminCreateRequest = {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        department: values.department,
        position: values.position,
        salary: values.salary,
        workingHours,
        emergencyContact,
      };

      createCommunalAdmin(payload, {
        onSuccess: (response) => {
          onSuccess();
        },
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  // Custom password validation
  const validatePasswordField = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Le mot de passe est requis'));
    }
    
    const validation = validatePassword(value);
    if (!validation.isValid) {
      return Promise.reject(new Error(validation.errors[0]));
    }
    
    return Promise.resolve();
  };

  // Phone validation
  const validatePhoneField = (_: any, value: string) => {
    if (value && !validatePhone(value)) {
      return Promise.reject(new Error('Format de téléphone invalide (ex: 226xxxxxxxx)'));
    }
    return Promise.resolve();
  };

  // Get password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { percent: 0, status: 'exception' as const, text: 'Aucun' };
    
    const validation = validatePassword(password);
    const strength = Math.max(0, (5 - validation.errors.length) * 20);
    
    if (strength >= 80) return { percent: strength, status: 'success' as const, text: 'Fort' };
    if (strength >= 60) return { percent: strength, status: 'normal' as const, text: 'Moyen' };
    if (strength >= 40) return { percent: strength, status: 'active' as const, text: 'Faible' };
    return { percent: strength, status: 'exception' as const, text: 'Très faible' };
  };

  const passwordValue = Form.useWatch('password', form) || '';
  const passwordStrength = getPasswordStrength(passwordValue);

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Créer un Administrateur Communal
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isCreating}
          onClick={handleSubmit}
          icon={<UserOutlined />}
        >
          Créer l'Administrateur
        </Button>,
      ]}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Information Alert */}
        <Alert
          message="Création d'un Administrateur Communal"
          description="L'administrateur communal aura accès à la gestion complète de sa commune assignée, incluant les groupes, membres, finances et rapports. Un mot de passe temporaire sera généré et devra être changé lors de la première connexion."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            department: 'Administration',
            position: 'Administrateur Communal',
            includeWorkingHours: false,
            includeEmergencyContact: false,
          }}
        >
          {/* Basic Information */}
          <Card title="Informations Personnelles" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label="Prénom"
                  rules={[
                    { required: true, message: 'Le prénom est requis' },
                    { min: 2, max: 50, message: 'Le prénom doit contenir entre 2 et 50 caractères' },
                  ]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Prénom" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label="Nom de famille"
                  rules={[
                    { required: true, message: 'Le nom de famille est requis' },
                    { min: 2, max: 50, message: 'Le nom doit contenir entre 2 et 50 caractères' },
                  ]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Nom de famille" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'L\'email est requis' },
                    { type: 'email', message: 'Format email invalide' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="email@example.com"
                    onChange={handleEmailChange}
                    suffix={
                      checkingEmail ? (
                        <ReloadOutlined spin />
                      ) : emailAvailable === true ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : emailAvailable === false ? (
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                      ) : null
                    }
                  />
                </Form.Item>
                {emailAvailable === false && (
                  <Text type="danger" style={{ fontSize: '12px' }}>
                    Cette adresse email est déjà utilisée
                  </Text>
                )}
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Téléphone (optionnel)"
                  rules={[{ validator: validatePhoneField }]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="226xxxxxxxx"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Authentication */}
          <Card title="Authentification" size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              name="password"
              label={
                <Space>
                  Mot de passe temporaire
                  <Tooltip title="Générer un mot de passe sécurisé">
                    <Button
                      type="link"
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={handleGeneratePassword}
                    >
                      Générer
                    </Button>
                  </Tooltip>
                </Space>
              }
              rules={[{ validator: validatePasswordField }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Mot de passe temporaire"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            {passwordValue && (
              <div style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: '12px', color: '#666' }}>
                  Force du mot de passe: <Text strong>{passwordStrength.text}</Text>
                </Text>
                <Progress
                  percent={passwordStrength.percent}
                  status={passwordStrength.status}
                  showInfo={false}
                  size="small"
                  style={{ marginTop: 4 }}
                />
              </div>
            )}

            {passwordGenerated && (
              <Alert
                message="Mot de passe généré"
                description="Un mot de passe sécurisé a été généré. L'utilisateur devra le changer lors de sa première connexion."
                type="success"
                size="small"
                style={{ marginTop: 8 }}
              />
            )}
          </Card>


          {/* Professional Details */}
          <Card title="Détails Professionnels" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="department" label="Département">
                  <Input placeholder="Administration" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="position" label="Poste">
                  <Input placeholder="Administrateur Communal" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="salary" label="Salaire (FCFA)">
                  <InputNumber
                    placeholder="500000"
                    style={{ width: '100%' }}
                    min={0}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Optional Sections */}
          <Collapse ghost>
            <Panel
              header="Heures de travail (optionnel)"
              key="working-hours"
              extra={
                <Form.Item name="includeWorkingHours" style={{ margin: 0 }}>
                  <Switch size="small" />
                </Form.Item>
              }
            >
              <Form.Item dependencies={['includeWorkingHours']}>
                {({ getFieldValue }) => {
                  const includeWorkingHours = getFieldValue('includeWorkingHours');
                  if (!includeWorkingHours) return null;

                  return (
                    <Card size="small">
                      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        Définissez les heures de travail pour chaque jour de la semaine
                      </Text>
                      {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'].map((day, index) => {
                        const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'][index];
                        return (
                          <Row key={dayKey} gutter={16} align="middle" style={{ marginBottom: 8 }}>
                            <Col span={4}>
                              <Text>{day}</Text>
                            </Col>
                            <Col span={6}>
                              <Form.Item name={['workingHours', dayKey, 'start']} style={{ margin: 0 }}>
                                <TimePicker
                                  format="HH:mm"
                                  placeholder="Début"
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item name={['workingHours', dayKey, 'end']} style={{ margin: 0 }}>
                                <TimePicker
                                  format="HH:mm"
                                  placeholder="Fin"
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item name={['workingHours', dayKey, 'isWorkingDay']} valuePropName="checked" style={{ margin: 0 }}>
                                <Switch size="small" /> <Text type="secondary">Jour travaillé</Text>
                              </Form.Item>
                            </Col>
                          </Row>
                        );
                      })}
                    </Card>
                  );
                }}
              </Form.Item>
            </Panel>

            <Panel
              header="Contact d'urgence (optionnel)"
              key="emergency-contact"
              extra={
                <Form.Item name="includeEmergencyContact" style={{ margin: 0 }}>
                  <Switch size="small" />
                </Form.Item>
              }
            >
              <Form.Item dependencies={['includeEmergencyContact']}>
                {({ getFieldValue }) => {
                  const includeEmergencyContact = getFieldValue('includeEmergencyContact');
                  if (!includeEmergencyContact) return null;

                  return (
                    <Card size="small">
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name={['emergencyContact', 'name']} label="Nom complet">
                            <Input prefix={<ContactsOutlined />} placeholder="Nom du contact" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name={['emergencyContact', 'phone']} label="Téléphone">
                            <Input prefix={<PhoneOutlined />} placeholder="226xxxxxxxx" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name={['emergencyContact', 'relationship']} label="Relation">
                            <Select placeholder="Sélectionner">
                              <Option value="spouse">Époux/Épouse</Option>
                              <Option value="parent">Parent</Option>
                              <Option value="child">Enfant</Option>
                              <Option value="sibling">Frère/Sœur</Option>
                              <Option value="friend">Ami(e)</Option>
                              <Option value="other">Autre</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                }}
              </Form.Item>
            </Panel>
          </Collapse>
        </Form>
      </div>
    </Modal>
  );
};

export default CommunalAdminCreateModal;